/**
 * Поведение модального слоя: палитра, лист, панель детализации, смена пароля.
 *
 * - фокус переходит внутрь при открытии и возвращается туда, откуда открыли;
 * - Tab и Shift+Tab ходят по кругу внутри слоя и не уходят на страницу под ним;
 * - Escape закрывает слой из любой точки, а не только из поля ввода;
 * - фон под слоем не прокручивается.
 *
 * Слои могут лежать друг на друге (смена пароля поверх листа «Ещё»):
 * клавиши обрабатывает только верхний, прокрутка фона возвращается,
 * когда закрыт последний.
 */

import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.getClientRects().length > 0 && !element.closest('[aria-hidden="true"], [inert]'),
  )
}

const stack: symbol[] = []
let scrollLocks = 0
let savedOverflow = ''

function lockScroll(): () => void {
  if (scrollLocks === 0) {
    savedOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  scrollLocks += 1
  return () => {
    scrollLocks -= 1
    if (scrollLocks === 0) document.body.style.overflow = savedOverflow
  }
}

export interface DialogOptions {
  /** Слой открыт: ловушка фокуса, Escape и блокировка прокрутки работают. */
  active: boolean
  onClose: () => void
  /** Что сфокусировать при открытии; по умолчанию — первое доступное внутри. */
  initialFocus?: RefObject<HTMLElement | null>
  /**
   * Куда вернуть фокус при закрытии, если элемент, с которого открыли,
   * успел исчезнуть (пункт закрывшегося меню). false — не возвращать:
   * слой делает это сам (панель детализации возвращает фокус в строку).
   */
  returnFocus?: (() => HTMLElement | null | undefined) | false
}

export function useDialog(ref: RefObject<HTMLElement | null>, { active, onClose, initialFocus, returnFocus }: DialogOptions): void {
  const close = useRef(onClose)
  close.current = onClose
  const fallback = useRef(returnFocus)
  fallback.current = returnFocus

  useEffect(() => {
    if (!active) return
    const id = Symbol('dialog')
    stack.push(id)
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const unlock = lockScroll()

    // Слой может смонтироваться кадром позже (лист ждёт своего mounted),
    // поэтому фокус ставится, как только он появился, — до десяти кадров.
    let frame = 0
    let tries = 0
    const place = () => {
      const root = ref.current
      if (!root) {
        if (tries++ < 10) frame = requestAnimationFrame(place)
        return
      }
      if (root.contains(document.activeElement)) return
      const target = initialFocus?.current ?? focusables(root)[0] ?? root
      target.focus({ preventScroll: true })
    }
    frame = requestAnimationFrame(place)

    const onKey = (event: KeyboardEvent) => {
      if (stack[stack.length - 1] !== id) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        close.current()
        return
      }
      if (event.key !== 'Tab') return
      const root = ref.current
      if (!root) return
      const items = focusables(root)
      if (items.length === 0) {
        event.preventDefault()
        root.focus({ preventScroll: true })
        return
      }
      const first = items[0]!
      const last = items[items.length - 1]!
      const current = document.activeElement
      const inside = current instanceof Node && root.contains(current)
      if (event.shiftKey && (current === first || !inside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (current === last || !inside)) {
        event.preventDefault()
        first.focus()
      }
    }
    // Фокус ушёл мышью на страницу под слоем — вернуть внутрь.
    const onFocusIn = (event: FocusEvent) => {
      if (stack[stack.length - 1] !== id) return
      const root = ref.current
      if (root && event.target instanceof Node && !root.contains(event.target)) {
        ;(focusables(root)[0] ?? root).focus({ preventScroll: true })
      }
    }
    document.addEventListener('keydown', onKey, true)
    document.addEventListener('focusin', onFocusIn)

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKey, true)
      document.removeEventListener('focusin', onFocusIn)
      const index = stack.indexOf(id)
      if (index >= 0) stack.splice(index, 1)
      unlock()
      if (fallback.current === false) return
      const target = opener?.isConnected ? opener : fallback.current?.()
      // После снятия слоя: иначе фокус «перепрыгнет» обратно до размонтирования.
      if (target) requestAnimationFrame(() => target.focus({ preventScroll: true }))
    }
  }, [active])
}
